'use client';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGed, userName } from '@/lib/store';
import { fetchEcriture } from '@/lib/ecrire';
import { peut, fmtDate, isoJour, aujourdhuiIso, fmtDateHeure } from '@/lib/meta';
import { I, Modale, card, inputStyle, useBusy, Skeleton, EmptyState } from '@/components/ui';
import { H1 } from './commun';
import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { Graphe, type Point as PointKpi, type Def as DefKpi, type Forme } from '../Kpi';
import { Tableau, type ColonneTriable, enTeteCollant } from '@/shared/Tableau';

/* ─────────────── QUALITÉ ANALYTIQUE — l'écran TRANSVERSAL du CQ ───────────────
 *
 * Chantier CQ (07-08/10/2026). Quatre lectures, UNE entrée de menu : la barre
 * latérale plafonne à dix entrées par famille (`front-familles-nav`), et la
 * famille « Contrôles & performance » en comptait neuf. Les quatre vues sont
 * donc des ONGLETS de cet écran : tableau de bord (R5), performance (R4), audit
 * mesure par mesure (R7), synthèse mensuelle (R8).
 *
 * Tout est DÉRIVÉ par le moteur à chaque lecture — rien n'est figé ici, et ce
 * qui ne se calcule pas est listé avec ce qui lui manque, nommément.
 * ------------------------------------------------------------------------- */

type Onglet = 'tdb' | 'perf' | 'audit' | 'synthese';

/**
 * UNE LECTURE À LA FOIS PAR ADRESSE (lot A, 08/10/2026). En développement,
 * React joue chaque effet deux fois : deux balayages identiques partaient en
 * même temps vers le moteur. Les lectures en cours sont partagées par URL ;
 * la carte est vidée dès la réponse, rien n'est gardé entre deux lectures —
 * le cache qui dure est côté moteur, invalidé par signature.
 */
const EN_COURS = new Map<string, Promise<Response>>();
function lire(url: string): Promise<Response> {
  const deja = EN_COURS.get(url);
  if (deja) return deja;
  const p = fetch(url, { credentials: 'include' }).finally(() => EN_COURS.delete(url));
  EN_COURS.set(url, p);
  return p;
}
/** Ce que les listes de filtres ont besoin de savoir d'une carte — servi par le magasin, sans appel de plus. */
type CarteFiltre = { equipement: string; equipementNom: string; discipline: string | null; analyte: string; analyteNom: string };
const ONGLETS: { k: Onglet; label: string }[] = [
  { k: 'tdb', label: 'Tableau de bord' },
  { k: 'perf', label: 'Performance (Sigma)' },
  { k: 'audit', label: 'Audit mesure par mesure' },
  { k: 'synthese', label: 'Synthèse mensuelle' },
];

/* ═══════════════════════════ R4 — PERFORMANCE ═══════════════════════════ */

type NiveauPerf = {
  niveau: number; n: number; cv: number; biais: number | null; tea: number | null;
  sigma: number | null; qgi: number | null; erreurTotaleObservee: number | null;
  classe: string | null; classeLibelle: string | null; calculable: 'complete' | 'provisoire' | 'impossible';
  motif?: string; dominante?: 'imprecision' | 'mixte' | 'biais'; conformeTea?: boolean | null;
  moyenne: number | null; sd: number | null; cibleFournisseur: number | null; teaProvenance: string;
};
type CartePerf = {
  carteId: string; analyte: string; analyteNom: string; equipement: string; equipementNom: string;
  discipline: string | null; unite: string | null; libellesNiveaux: string[];
  /** Convention d'arrondi du paramètre (`ged-analyte.decimales`) ; `null` = non fixée. */
  decimales: number | null;
  lotControle: string | null; periodeId: string | null; sourceCible: string | null;
  teaSource: 'referentiel' | 'carte' | 'aucune'; teaCritere: { documentId: string; libelle: string; source: string | null; colonne: string | null } | null;
  teaReference: string | null; fenetre: { du: string | null; au: string | null; points: number };
  minPointsInterne: number; reglesActives: string[];
  niveaux: NiveauPerf[];
  proposition: { propose: boolean; motif: string; profil: string[]; manquantes: string[] };
  aParametrer: boolean;
};
type RecapPerf = {
  cartes: number; lignes: number; completes: number; provisoires: number; impossibles: number;
  sansTea: number; sousQuatreSigma: number; propositionsProfil: number; parClasse: Record<string, number>;
};

const TON_CLASSE: Record<string, { bg: string; fg: string }> = {
  non_acceptable: { bg: 'var(--red-50)', fg: 'var(--red-600)' },
  faible: { bg: 'var(--red-50)', fg: 'var(--red-600)' },
  limite: { bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  bonne: { bg: 'var(--green-50)', fg: 'var(--green-600)' },
  excellente: { bg: 'var(--green-50)', fg: 'var(--green-600)' },
  classe_mondiale: { bg: 'var(--cyan-50)', fg: 'var(--cyan-700)' },
};
const DOMINANTE: Record<string, string> = { imprecision: 'imprécision', mixte: 'mixte', biais: 'biais' };
const SOURCE_TEA: Record<string, string> = { referentiel: 'référentiel', carte: 'propre à la carte', aucune: 'aucune' };

const fmt = (v: number | null | undefined, d = 2) => (v === null || v === undefined ? '—' : v.toFixed(d));
/** Une valeur dans l'unité du paramètre : à sa convention de décimales, sinon trois chiffres significatifs. */
const fmtMesure = (v: number | null | undefined, decimales: number | null) => {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  if (typeof decimales === 'number') return v.toFixed(decimales);
  return Number(v.toPrecision(3)).toString();
};

function Performance({ onOuvrirCarte }: { onOuvrirCarte: (carteId: string) => void }) {
  const { session, settings } = useGed();
  const peutParametrer = peut(settings?.perms, session.profile, 'cqiParametrer');
  const [cartes, setCartes] = useState<CartePerf[] | null>(null);
  const [recap, setRecap] = useState<RecapPerf | null>(null);
  const [erreur, setErreur] = useState('');
  const [filtreEquip, setFiltreEquip] = useState('');
  const [filtreEtat, setFiltreEtat] = useState<'' | 'complete' | 'provisoire' | 'impossible' | 'sous4' | 'sansTea'>('');
  const [recherche, setRecherche] = useState('');
  const [teaPour, setTeaPour] = useState<CartePerf | null>(null);
  const [teaEnLot, setTeaEnLot] = useState(false);

  /* La lecture seule, pour l'effet ; `charger` (boutons) efface d'abord
     l'erreur précédente — au montage, il n'y en a pas. */
  const lirePerformance = useCallback(async () => {
    try {
      const r = await lire('/api/ged/ged/cq/performance');
      if (!r.ok) { setErreur(`La performance n’a pas pu être lue (HTTP ${r.status}). Ce n’est pas un registre vide.`); setCartes([]); return; }
      const j = await r.clone().json();
      setCartes(j.cartes || []); setRecap(j.recapitulatif || null);
    } catch { setErreur('La performance n’a pas pu être lue — le moteur ne répond pas.'); setCartes([]); }
  }, []);
  const charger = useCallback(() => { setErreur(''); return lirePerformance(); }, [lirePerformance]);
  /* ⚠️ FAUX POSITIF ASSUMÉ de `react-hooks/set-state-in-effect` (même motif
     que `AccesAudits.tsx`) : `lirePerformance` est ASYNC et n'écrit l'état
     qu'APRÈS son `await`. Silence ciblé, ligne à ligne. */
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { void lirePerformance(); }, [lirePerformance]);

  const equipements = useMemo(() => [...new Set((cartes || []).map((c) => `${c.equipement}|${c.equipementNom}`))].sort(), [cartes]);
  const lignes = useMemo(() => (cartes || [])
    .filter((c) => !filtreEquip || c.equipement === filtreEquip)
    .filter((c) => !recherche || `${c.analyte} ${c.analyteNom} ${c.equipementNom}`.toLowerCase().includes(recherche.toLowerCase()))
    .flatMap((c) => (c.niveaux.length ? c.niveaux.map((n) => ({ c, n })) : [{ c, n: null as NiveauPerf | null }]))
    .filter(({ c, n }) => {
      if (!filtreEtat) return true;
      if (filtreEtat === 'sansTea') return c.teaSource === 'aucune';
      if (filtreEtat === 'sous4') return !!n && n.sigma !== null && n.sigma < 4;
      return (n?.calculable || 'impossible') === filtreEtat;
    }), [cartes, filtreEquip, filtreEtat, recherche]);

  if (cartes === null) return <Skeleton rows={6} />;
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {erreur && <div style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--red-50)', color: 'var(--red-600)', fontSize: 12.5 }}>{erreur}</div>}
      {recap && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            { k: '', n: recap.lignes, label: 'lignes (carte × niveau)' },
            { k: 'complete', n: recap.completes, label: 'calculées' },
            { k: 'provisoire', n: recap.provisoires, label: 'provisoires (n insuffisant)' },
            { k: 'impossible', n: recap.impossibles, label: 'non calculables' },
            { k: 'sansTea', n: recap.sansTea, label: 'cartes sans TEa' },
            { k: 'sous4', n: recap.sousQuatreSigma, label: 'sous 4 sigma' },
          ].map((x) => (
            <button key={x.k || 'tout'} type="button" onClick={() => setFiltreEtat(x.k as typeof filtreEtat)}
              title={`Filtrer : ${x.label}`}
              style={{ ...card, padding: '8px 12px', cursor: 'pointer', border: filtreEtat === x.k ? '2px solid var(--color-primary-active)' : undefined, textAlign: 'left' }}>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{x.n}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{x.label}</div>
            </button>
          ))}
          <div style={{ ...card, padding: '8px 12px', fontSize: 11.5, color: 'var(--text-muted)' }}>
            {Object.entries(recap.parClasse).map(([k, n]) => `${k.replace('_', ' ')} : ${n}`).join(' · ')}
          </div>
        </div>
      )}
      <div style={{ ...card, padding: 12, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
          <span>Recherche (paramètre, automate)</span>
          <input value={recherche} onChange={(e) => setRecherche(e.target.value)} style={{ ...inputStyle, width: 220 }} placeholder="ASAT, Indiko…" />
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
          <span>Automate</span>
          <select value={filtreEquip} onChange={(e) => setFiltreEquip(e.target.value)} style={{ ...inputStyle, width: 220 }}>
            <option value="">Tous</option>
            {equipements.map((e) => { const [code, nom] = e.split('|'); return <option key={code} value={code}>{code} · {nom}</option>; })}
          </select>
        </label>
        <span style={{ flex: 1 }} />
        <Bouton taille="sm" onClick={() => setTeaEnLot(true)} disabled={!peutParametrer} title={peutParametrer ? 'Choisir la source de la TEa pour toutes les cartes d’un automate en une séance (aperçu, puis écriture)' : 'Le choix de la TEa exige le droit « cqiParametrer »'}>TEa en lot…</Bouton>
        <Bouton taille="sm" onClick={() => void charger()}>Recalculer</Bouton>
      </div>
      {!lignes.length ? (
        <EmptyState titre="Aucune ligne pour ce filtre" indice="Les cartes non calculables sont listées comme les autres : élargissez le filtre." />
      ) : (
        <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
          <table ref={enTeteCollant} className="ged-tableau" style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1100, fontSize: 12.5 }}>
            <thead>
              <tr style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                {['Carte', 'Niveau', 'n', 'Moyenne', 'σ', 'CV %', 'Biais %', 'TEa % · source', 'Sigma', 'Classe', 'QGI · dominante', 'ETo %', 'Fenêtre', ''].map((h) => (
                  <th scope="col" key={h} style={{ textAlign: 'left', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lignes.map(({ c, n }, i) => {
                const ton = n?.classe ? TON_CLASSE[n.classe] : null;
                const nom = n ? (c.libellesNiveaux[n.niveau - 1]?.trim() || `Niveau ${n.niveau}`) : '—';
                return (
                  <tr key={`${c.carteId}-${n?.niveau ?? 0}-${i}`} style={{ borderTop: '1px solid var(--border-default)', background: n?.calculable === 'impossible' ? 'var(--surface-sunken)' : undefined }}>
                    <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>
                      <button type="button" onClick={() => onOuvrirCarte(c.carteId)} title="Ouvrir la carte de contrôle"
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 700, color: 'var(--color-primary-active)', textAlign: 'left' }}>
                        {c.analyteNom} <I n="arrow" s={11} />
                      </button>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{c.equipement} · {c.equipementNom}{c.lotControle ? ` · lot ${c.lotControle}` : ''}</div>
                      {c.proposition?.propose && (
                        <div style={{ fontSize: 11, color: 'var(--amber-600)', fontWeight: 600, marginTop: 2 }} title={c.proposition.motif}>
                          ⚑ profil de règles renforcé proposé ({c.proposition.manquantes.join(', ')}) — à décider sur la carte
                        </div>
                      )}
                    </td>
                    <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>{nom}</td>
                    <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', fontFamily: 'var(--font-mono)' }}>{n ? n.n : '—'}</td>
                    <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', fontFamily: 'var(--font-mono)' }} title={n && n.n ? `${n.moyenne}` : undefined}>{n && n.n ? fmtMesure(n.moyenne, c.decimales) : '—'}</td>
                    <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', fontFamily: 'var(--font-mono)' }} title={n && n.n ? `${n.sd}` : undefined}>{n && n.n ? fmtMesure(n.sd, c.decimales === null ? null : c.decimales + 1) : '—'}</td>
                    <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', fontFamily: 'var(--font-mono)' }}>{n && n.n ? fmt(n.cv) : '—'}</td>
                    <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', fontFamily: 'var(--font-mono)' }}>{n && n.biais !== null ? `${n.biais > 0 ? '+' : ''}${fmt(n.biais)}` : '—'}</td>
                    <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>
                      <span style={{ fontFamily: 'var(--font-mono)' }}>{n && n.tea !== null ? fmt(n.tea, 1) : '—'}</span>
                      <div style={{ fontSize: 10.5, color: c.teaSource === 'aucune' ? 'var(--amber-600)' : 'var(--text-muted)' }} title={n?.teaProvenance}>{n?.teaProvenance || SOURCE_TEA[c.teaSource]}</div>
                    </td>
                    <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{n && n.sigma !== null ? fmt(n.sigma, 1) : '—'}</td>
                    <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>
                      {n?.classe && ton ? (
                        <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 8px', background: ton.bg, color: ton.fg }}>
                          {n.classeLibelle}{n.calculable === 'provisoire' ? ' (provisoire)' : ''}
                        </span>
                      ) : (
                        <span style={{ fontSize: 11.5, color: 'var(--amber-600)', fontWeight: 600 }} title="Ce qui manque pour calculer">
                          non calculable — {n?.motif || (c.aParametrer ? 'aucun lot ouvert' : 'aucun niveau chiffré')}
                        </span>
                      )}
                      {n?.calculable === 'provisoire' && n.motif && <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>{n.motif}</div>}
                    </td>
                    <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', fontFamily: 'var(--font-mono)' }}>
                      {n && n.qgi !== null ? `${fmt(n.qgi)} · ${DOMINANTE[n.dominante || ''] || ''}` : (n && n.sigma !== null ? '≥ 4 σ : sans objet' : '—')}
                    </td>
                    <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', fontFamily: 'var(--font-mono)', color: n?.conformeTea === false ? 'var(--red-600)' : undefined }}>
                      {n && n.erreurTotaleObservee !== null ? fmt(n.erreurTotaleObservee) : '—'}
                    </td>
                    <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', fontSize: 'var(--tableau-ligne-police)', color: 'var(--text-muted)' }}>
                      {c.fenetre.du ? `${fmtDate(c.fenetre.du)} → ${c.fenetre.au ? fmtDate(c.fenetre.au) : '?'} · ${c.fenetre.points} pts` : 'aucun passage'}
                    </td>
                    <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>
                      {(!n || n.niveau === c.niveaux[0]?.niveau) && (
                        <Bouton taille="sm" onClick={() => setTeaPour(c)}
                          title={peutParametrer ? 'Choisir la source de la TEa : référentiel, propre à la carte, ou aucune' : 'Consulter les sources de TEa (le choix exige le droit « cqiParametrer »)'}>
                          TEa…
                        </Bouton>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Sigma = (TEa − |biais|) / CV · QGI = |biais| / (1,5 × CV), calculé seulement sous 4 sigma · ETo = |biais| + 1,65 × CV.
        Une ligne non calculable dit ce qui lui manque ; une valeur provisoire est calculée sur moins de points que le minimum de la carte.
        Un sigma sous 4 <strong>propose</strong> le profil de règles renforcé — rien n’est changé sans décision sur la carte.
      </p>
      {teaPour && (
        <ChoixTea carte={teaPour} peutChoisir={peutParametrer} onClose={() => setTeaPour(null)}
          onChoisi={() => { setTeaPour(null); void charger(); }} />
      )}
      {teaEnLot && (
        <TeaEnLot automateInitial={filtreEquip} onClose={() => setTeaEnLot(false)} onApplique={() => { void charger(); }} />
      )}
    </div>
  );
}

/* ── Lot B — LA TEa POUR TOUT UN AUTOMATE EN UNE SÉANCE ────────────────────
 *
 * 132 cartes sans TEa, 132 fenêtres : personne ne les ouvre. Ici on choisit UNE
 * source et UNE colonne, le moteur montre carte par carte ce qu'il trouve, le
 * rattachement Bas/Moyen/Élevé qu'il PROPOSE (modifiable), et ce qui empêche
 * d'appliquer. A1 tient : rien n'est deviné, l'utilisateur confirme, et les
 * cartes sans critère sont écartées avec leur motif, jamais remplies à défaut. */
type ApercuLot = {
  automate: string | null; sourceCode: string | null; colonne: string;
  sources: { code: string; libelle: string; version: string }[];
  colonnes: { cle: string; libelle: string }[];
  lignes: {
    carteId: string; analyte: string; analyteNom: string; equipement: string; libellesNiveaux: string[]; unite: string | null;
    teaActuelle: string;
    critere: { documentId: string; libelle: string; concentrations: { bas: number | null; moyen: number | null; eleve: number | null }; colonnes: { cle: string; libelle: string; bas: number | null; moyen: number | null; eleve: number | null }[] } | null;
    rattachement: { niveau: number; cible: number; propose: 'bas' | 'moyen' | 'eleve' | null; tea: number | null }[];
    manques: string[]; applicable: boolean;
  }[];
  recapitulatif: { cartes: number; applicables: number; ecartees: number };
};

function TeaEnLot({ automateInitial, onClose, onApplique }: { automateInitial: string; onClose: () => void; onApplique: () => void }) {
  const { cartesCqi } = useGed();
  const equips = useMemo(() => [...new Map(cartesCqi.map((c) => [c.equipement, c.equipementNom])).entries()].sort(), [cartesCqi]);
  const [automate, setAutomate] = useState(automateInitial || equips[0]?.[0] || '');
  const [source, setSource] = useState<'referentiel' | 'carte' | 'aucune'>('referentiel');
  const [sourceCode, setSourceCode] = useState('');
  const [colonne, setColonne] = useState('inexactitude');
  const [valeur, setValeur] = useState('');
  const [reference, setReference] = useState('');
  const [apercu, setApercu] = useState<ApercuLot | null>(null);
  const [rattachements, setRattachements] = useState<Record<string, Record<string, string>>>({});
  const [exclues, setExclues] = useState<Set<string>>(new Set());
  const [erreur, setErreur] = useState('');
  const [resultat, setResultat] = useState<{ appliquees: { analyte: string }[]; ecartees: { analyte?: string; motif: string }[] } | null>(null);
  const { run, estOccupe } = useBusy();
  // Autre automate, source ou colonne : l'aperçu précédent s'efface pendant la requête.
  // Recalé PENDANT LE RENDU (motif de `ui.tsx`), pas dans l'effet.
  const cleApercu = JSON.stringify([automate, sourceCode, colonne]);
  const [apercuVu, setApercuVu] = useState(cleApercu);
  if (apercuVu !== cleApercu) { setApercuVu(cleApercu); setApercu(null); setErreur(''); }
  useEffect(() => {
    let vivant = true;
    void lire(`/api/ged/ged/cq/tea-en-lot?automate=${encodeURIComponent(automate)}&sourceCode=${encodeURIComponent(sourceCode)}&colonne=${encodeURIComponent(colonne)}`)
      .then(async (r) => (r.ok ? r.clone().json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j: ApercuLot) => {
        if (!vivant) return;
        setApercu(j);
        if (!sourceCode && j.sources.length) setSourceCode(j.sources[0]!.code);
        // Le rattachement PROPOSÉ pré-remplit ; l'utilisateur confirme ou corrige.
        setRattachements(Object.fromEntries(j.lignes.map((l) => [l.carteId, Object.fromEntries(l.rattachement.filter((r) => r.propose).map((r) => [String(r.niveau), r.propose as string]))])));
      })
      .catch((e) => { if (vivant) setErreur(`L’aperçu n’a pas pu être lu (${e.message}).`); });
    return () => { vivant = false; };
  }, [automate, sourceCode, colonne]);
  const lignes = apercu?.lignes || [];
  const retenues = lignes.filter((l) => !exclues.has(l.carteId) && (source !== 'referentiel' || l.applicable || (l.critere && Object.keys(rattachements[l.carteId] || {}).length > 0)));
  const nom = (l: ApercuLot['lignes'][number], n: number) => l.libellesNiveaux[n - 1]?.trim() || `Niveau ${n}`;
  const manque = source === 'referentiel' ? (!sourceCode ? 'choisir la source' : !retenues.length ? 'aucune carte applicable' : '')
    : source === 'carte' ? (!(Number(valeur.replace(',', '.')) > 0) ? 'une TEa en % > 0' : reference.trim().length < 3 ? 'la référence bibliographique' : !retenues.length ? 'aucune carte' : '') : (!retenues.length ? 'aucune carte' : '');
  const appliquer = () => run('lot', async () => {
    setErreur('');
    const r = await fetchEcriture('/api/ged/ged/cq/tea-en-lot', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(source === 'referentiel'
        ? { source, sourceCode, colonne, cartes: retenues.map((l) => ({ carteId: l.carteId, rattachement: rattachements[l.carteId] || {} })) }
        : source === 'carte' ? { source, teaValeur: valeur.replace(',', '.'), teaReference: reference, cartes: retenues.map((l) => ({ carteId: l.carteId })) }
          : { source, cartes: retenues.map((l) => ({ carteId: l.carteId })) }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { setErreur(j.error?.message || 'Refusé.'); return; }
    setResultat({ appliquees: j.appliquees || [], ecartees: j.ecartees || [] });
    onApplique();
  });
  const champ = { ...inputStyle, fontSize: 12.5 };
  return (
    <Modale titre="TEa en lot — un automate, une source, une séance" onClose={onClose} largeur={960}
      sousTitre="Le moteur montre ce qu’il trouve pour chaque carte et propose le rattachement Bas / Moyen / Élevé ; vous confirmez. Les cartes sans critère sont écartées avec leur motif, jamais remplies à défaut."
      pied={(
        <>
          <Bouton onClick={onClose}>{resultat ? 'Fermer' : 'Annuler'}</Bouton>
          {!resultat && (
            <Bouton variante="primaire" onClick={() => void appliquer()} disabled={!!manque || estOccupe() || !apercu}
              title={manque ? `Il manque : ${manque}` : `Écrit le choix sur ${retenues.length} carte(s), un maillon de journal par carte`}>
              Appliquer à {retenues.length} carte(s)
            </Bouton>
          )}
        </>
      )}>
      <div style={{ display: 'grid', gap: 12 }}>
        {erreur && <div style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</div>}
        {resultat ? (
          <div style={{ display: 'grid', gap: 6, fontSize: 12.5 }}>
            <strong>{resultat.appliquees.length} carte(s) mise(s) à jour{resultat.ecartees.length ? ` · ${resultat.ecartees.length} écartée(s)` : ''}</strong>
            {resultat.ecartees.map((e, i) => <span key={i} style={{ color: 'var(--amber-600)' }}>· {e.analyte || '?'} — {e.motif}</span>)}
          </div>
        ) : (<>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ display: 'grid', gap: 3, fontSize: 11.5 }}><span>Automate</span>
              <select value={automate} onChange={(e) => setAutomate(e.target.value)} style={{ ...champ, width: 220 }}>
                <option value="">Tous les automates</option>
                {equips.map(([c, n]) => <option key={c} value={c}>{c} · {n}</option>)}
              </select>
            </label>
            <label style={{ display: 'grid', gap: 3, fontSize: 11.5 }}><span>Source de la TEa</span>
              <select value={source} onChange={(e) => setSource(e.target.value as typeof source)} style={{ ...champ, width: 200 }}>
                <option value="referentiel">Référentiel</option><option value="carte">Propre à la carte (même valeur)</option><option value="aucune">Aucune</option>
              </select>
            </label>
            {source === 'referentiel' && (<>
              <label style={{ display: 'grid', gap: 3, fontSize: 11.5 }}><span>Société / publication</span>
                <select value={sourceCode} onChange={(e) => setSourceCode(e.target.value)} style={{ ...champ, width: 260 }}>
                  <option value="">— choisir —</option>
                  {(apercu?.sources || []).map((x) => <option key={x.code} value={x.code}>{x.libelle} {x.version}</option>)}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 3, fontSize: 11.5 }}><span>Colonne retenue</span>
                <select value={colonne} onChange={(e) => setColonne(e.target.value)} style={{ ...champ, width: 220 }}>
                  {(apercu?.colonnes || [{ cle: 'inexactitude', libelle: 'Inexactitude (erreur totale)' }]).map((c) => <option key={c.cle} value={c.cle}>{c.libelle}</option>)}
                </select>
              </label>
            </>)}
            {source === 'carte' && (<>
              <label style={{ display: 'grid', gap: 3, fontSize: 11.5 }}><span>TEa (%)</span><input value={valeur} onChange={(e) => setValeur(e.target.value)} inputMode="decimal" style={{ ...champ, width: 90 }} /></label>
              <label style={{ display: 'grid', gap: 3, fontSize: 11.5, flex: 1 }}><span>Référence (obligatoire)</span><input value={reference} onChange={(e) => setReference(e.target.value)} style={champ} /></label>
            </>)}
          </div>
          {apercu && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {apercu.recapitulatif.cartes} carte(s) · {source === 'referentiel' ? `${apercu.recapitulatif.applicables} applicable(s) · ${apercu.recapitulatif.ecartees} écartée(s), motif nommé` : `${retenues.length} retenue(s)`} · décochez une carte pour la laisser hors du lot
            </div>
          )}
          {!apercu ? <Skeleton rows={4} /> : (
            <div style={{ ...card, padding: 0, overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}>
              <table ref={enTeteCollant} className="ged-tableau" style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                <thead><tr style={{ fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                  <th scope="col" style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}></th><th scope="col" style={{ textAlign: 'left', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>Carte</th><th scope="col" style={{ textAlign: 'left', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>TEa actuelle</th>
                  {source === 'referentiel' && <><th scope="col" style={{ textAlign: 'left', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>Critère trouvé</th><th scope="col" style={{ textAlign: 'left', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>Rattachement par niveau → TEa</th></>}
                  <th scope="col" style={{ textAlign: 'left', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>Ce qui manque</th>
                </tr></thead>
                <tbody>
                  {lignes.map((l) => {
                    const dedans = retenues.some((r) => r.carteId === l.carteId);
                    return (
                      <tr key={l.carteId} style={{ borderTop: '1px solid var(--border-default)', opacity: dedans ? 1 : 0.55 }}>
                        <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}><input type="checkbox" checked={!exclues.has(l.carteId)} disabled={source === 'referentiel' && !l.critere} onChange={(e) => setExclues((x) => { const n = new Set(x); if (e.target.checked) n.delete(l.carteId); else n.add(l.carteId); return n; })} aria-label={`Inclure ${l.analyteNom}`} /></td>
                        <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}><strong>{l.analyteNom}</strong><div style={{ fontSize: 10.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{l.analyte} · {l.equipement}</div></td>
                        <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>{SOURCE_TEA[l.teaActuelle] || l.teaActuelle}</td>
                        {source === 'referentiel' && (<>
                          <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', fontSize: 'var(--tableau-ligne-police)' }}>{l.critere ? l.critere.libelle : <span style={{ color: 'var(--amber-600)' }}>aucun</span>}</td>
                          <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>
                            {l.critere && l.rattachement.map((r) => {
                              const choix = (rattachements[l.carteId] || {})[String(r.niveau)] || '';
                              const col = l.critere!.colonnes.find((c) => c.cle === colonne);
                              const tea = choix && col ? col[choix as 'bas' | 'moyen' | 'eleve'] : null;
                              return (
                                <div key={r.niveau} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11.5 }}>
                                  <span style={{ minWidth: 110 }}>{nom(l, r.niveau)} · {r.cible}{l.unite ? ` ${l.unite}` : ''}</span>
                                  <select value={choix} onChange={(e) => setRattachements((x) => ({ ...x, [l.carteId]: { ...(x[l.carteId] || {}), [String(r.niveau)]: e.target.value } }))} style={{ ...champ, width: 110, height: 26 }}>
                                    <option value="">—</option>
                                    <option value="bas">Bas{l.critere!.concentrations.bas !== null ? ` (${l.critere!.concentrations.bas})` : ''}</option>
                                    <option value="moyen">Moyen{l.critere!.concentrations.moyen !== null ? ` (${l.critere!.concentrations.moyen})` : ''}</option>
                                    <option value="eleve">Élevé{l.critere!.concentrations.eleve !== null ? ` (${l.critere!.concentrations.eleve})` : ''}</option>
                                  </select>
                                  <span style={{ fontFamily: 'var(--font-mono)', color: tea === null ? 'var(--amber-600)' : undefined }}>→ {tea === null ? 'pas de valeur' : `${tea} %`}</span>
                                  {r.propose && choix === r.propose && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>proposé</span>}
                                </div>
                              );
                            })}
                          </td>
                        </>)}
                        <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', fontSize: 'var(--tableau-ligne-police)', color: 'var(--amber-600)' }}>{l.manques.filter((m) => source === 'referentiel' || !/critère|source/.test(m)).join(' · ')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>)}
      </div>
    </Modale>
  );
}

/* ── A1 — les sources de TEa CÔTE À CÔTE, l'utilisateur tranche ──────────── */

type SourcesTea = {
  carteId: string; analyte: string | null; unite: string | null; libellesNiveaux: string[];
  choix: { teaSource: string; teaValeur: number | null; teaReference: string | null; teaCritereRef: string | null; teaColonne: string | null; teaNiveauSfbc: Record<string, string> | null; teaChoisieLe: string | null; teaChoisiePar: string | null };
  niveaux: { niveau: number; cible: number }[];
  referentiel: {
    documentId: string; libelle: string; unite: string | null;
    source: { code: string; libelle: string; version: string; referenceBiblio: string | null } | null;
    concentrations: { bas: number | null; moyen: number | null; eleve: number | null };
    colonnes: { cle: string; libelle: string; bas: number | null; moyen: number | null; eleve: number | null }[];
    niveauxProposes: Record<string, 'bas' | 'moyen' | 'eleve' | null>;
  }[];
};

function ChoixTea({ carte, peutChoisir, onClose, onChoisi }: { carte: CartePerf; peutChoisir: boolean; onClose: () => void; onChoisi: () => void }) {
  const { settings } = useGed();
  const [src, setSrc] = useState<SourcesTea | null>(null);
  const [erreur, setErreur] = useState('');
  const [source, setSource] = useState<'referentiel' | 'carte' | 'aucune'>(carte.teaSource);
  const [critereRef, setCritereRef] = useState(carte.teaCritere?.documentId || '');
  const [colonne, setColonne] = useState(carte.teaCritere?.colonne || '');
  const [rattachement, setRattachement] = useState<Record<string, string>>({});
  const [valeur, setValeur] = useState('');
  const [reference, setReference] = useState(carte.teaReference || '');
  const { run, estOccupe } = useBusy();

  useEffect(() => {
    let vivant = true;
    void fetch(`/api/ged/ged/cqi-cartes/${carte.carteId}/tea`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: SourcesTea | null) => {
        if (!vivant) return;
        if (!j) { setErreur('Les sources de TEa n’ont pas pu être lues.'); return; }
        setSrc(j);
        setValeur(j.choix.teaValeur === null ? '' : String(j.choix.teaValeur));
        setRattachement(j.choix.teaNiveauSfbc || {});
        if (!j.choix.teaCritereRef && j.referentiel.length === 1) setCritereRef(j.referentiel[0]!.documentId);
      });
    return () => { vivant = false; };
  }, [carte.carteId]);

  const critere = src?.referentiel.find((k) => k.documentId === critereRef) || null;
  // Le rattachement PROPOSÉ par le serveur sert de pré-remplissage, jamais de choix.
  // Recalé PENDANT LE RENDU (motif de `ui.tsx`), pas dans l'effet.
  const [critereVu, setCritereVu] = useState(critere);
  if (critereVu !== critere) {
    setCritereVu(critere);
    if (critere) {
      setRattachement((r) => {
        if (Object.keys(r).length) return r;
        const prop: Record<string, string> = {};
        for (const [niv, n] of Object.entries(critere.niveauxProposes)) if (n) prop[niv] = n;
        return prop;
      });
    }
  }

  const nom = (n: number) => src?.libellesNiveaux[n - 1]?.trim() || `Niveau ${n}`;
  const manque = source === 'referentiel'
    ? (!critereRef ? 'choisir un critère du référentiel' : !colonne ? 'choisir la colonne retenue' : (src?.niveaux || []).some((n) => !rattachement[String(n.niveau)]) ? 'rattacher chaque niveau à Bas, Moyen ou Élevé' : '')
    : source === 'carte' ? (!(Number(valeur.replace(',', '.')) > 0) ? 'une TEa en % strictement positive' : reference.trim().length < 3 ? 'la référence bibliographique' : '') : '';
  const enregistrer = () => run('tea', async () => {
    const r = await fetchEcriture(`/api/ged/ged/cqi-cartes/${carte.carteId}/tea`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(source === 'referentiel'
        ? { teaSource: source, teaCritereRef: critereRef, teaColonne: colonne, teaNiveauSfbc: rattachement }
        : source === 'carte' ? { teaSource: source, teaValeur: valeur.replace(',', '.'), teaReference: reference } : { teaSource: source }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { setErreur(j.error?.message || 'Choix refusé.'); return; }
    onChoisi();
  });
  const bloc = (actif: boolean): React.CSSProperties => ({ ...card, padding: 12, border: actif ? '2px solid var(--color-primary-active)' : undefined, display: 'grid', gap: 8 });

  return (
    <Modale titre={`TEa — ${carte.analyteNom} · ${carte.equipement}`} onClose={onClose} largeur={860}
      sousTitre={src?.choix.teaChoisieLe ? `Choix actuel : ${SOURCE_TEA[src.choix.teaSource]} — le ${fmtDate(src.choix.teaChoisieLe)} par ${userName(settings, src.choix.teaChoisiePar)}` : 'Aucun choix enregistré : la performance est non calculable'}
      pied={(
        <>
          <Bouton onClick={onClose}>Fermer</Bouton>
          <Bouton variante="primaire" onClick={() => void enregistrer()} disabled={!peutChoisir || !!manque || estOccupe()}
            title={!peutChoisir ? 'Le choix exige le droit « cqiParametrer »' : manque ? `Il manque : ${manque}` : 'Écrit le choix sur la carte, au journal'}>
            Enregistrer ce choix
          </Bouton>
        </>
      )}>
      {!src ? <Skeleton rows={3} /> : (
        <div style={{ display: 'grid', gap: 12 }}>
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
            Les sources sont présentées côte à côte ; rien n’est deviné ni imposé. Le choix est écrit sur la carte et tracé.
          </p>
          {erreur && <div style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</div>}

          <div style={bloc(source === 'referentiel')}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 700, fontSize: 13 }}>
              <input type="radio" checked={source === 'referentiel'} onChange={() => setSource('referentiel')} disabled={!src.referentiel.length} />
              Référentiel des critères de performance
              {!src.referentiel.length && <span style={{ fontWeight: 400, color: 'var(--amber-600)', fontSize: 12 }}>— aucun critère ne vise l’analyte {src.analyte} : à créer dans « Critères de performance »</span>}
            </label>
            {source === 'referentiel' && src.referentiel.length > 0 && (
              <div style={{ display: 'grid', gap: 8 }}>
                <select value={critereRef} onChange={(e) => { setCritereRef(e.target.value); setColonne(''); setRattachement({}); }} style={inputStyle} aria-label="Critère du référentiel">
                  <option value="">— choisir le critère —</option>
                  {src.referentiel.map((k) => <option key={k.documentId} value={k.documentId}>{k.libelle}{k.source ? ` — ${k.source.libelle} ${k.source.version}` : ''}{k.unite ? ` (${k.unite})` : ''}</option>)}
                </select>
                {critere && (
                  <>
                    {critere.source?.referenceBiblio && <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{critere.source.referenceBiblio}</div>}
                    <table ref={enTeteCollant} className="ged-tableau" style={{ borderCollapse: 'collapse', fontSize: 12.5 }}>
                      <thead>
                        <tr style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                          <th scope="col" style={{ textAlign: 'left', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>Colonne retenue</th>
                          <th scope="col" style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>Bas{critere.concentrations.bas !== null ? ` (${critere.concentrations.bas})` : ''}</th>
                          <th scope="col" style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>Moyen{critere.concentrations.moyen !== null ? ` (${critere.concentrations.moyen})` : ''}</th>
                          <th scope="col" style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>Élevé{critere.concentrations.eleve !== null ? ` (${critere.concentrations.eleve})` : ''}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {critere.colonnes.map((col) => (
                          <tr key={col.cle} style={{ borderTop: '1px solid var(--border-default)', background: colonne === col.cle ? 'var(--cyan-50)' : undefined }}>
                            <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>
                              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <input type="radio" name="colonne" checked={colonne === col.cle} onChange={() => setColonne(col.cle)} />{col.libelle}
                              </label>
                            </td>
                            {(['bas', 'moyen', 'eleve'] as const).map((k) => <td key={k} style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{col[k] === null ? '—' : `${col[k]} %`}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
                      <strong>Rattachement de chaque niveau de contrôle (proposé d’après la cible, à confirmer)</strong>
                      {src.niveaux.map((n) => (
                        <label key={n.niveau} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span style={{ minWidth: 160 }}>{nom(n.niveau)} · cible {n.cible}{src.unite ? ` ${src.unite}` : ''}</span>
                          <select value={rattachement[String(n.niveau)] || ''} onChange={(e) => setRattachement((r) => ({ ...r, [String(n.niveau)]: e.target.value }))} style={{ ...inputStyle, width: 160 }}>
                            <option value="">— choisir —</option>
                            <option value="bas">Bas</option><option value="moyen">Moyen</option><option value="eleve">Élevé</option>
                          </select>
                          {critere.niveauxProposes[String(n.niveau)] && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>proposé : {critere.niveauxProposes[String(n.niveau)]}</span>}
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div style={bloc(source === 'carte')}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 700, fontSize: 13 }}>
              <input type="radio" checked={source === 'carte'} onChange={() => setSource('carte')} />
              Propre à la carte — valeur saisie, référence bibliographique obligatoire
            </label>
            {source === 'carte' && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <label style={{ display: 'grid', gap: 3, fontSize: 12 }}><span>TEa (%)</span><input value={valeur} onChange={(e) => setValeur(e.target.value)} inputMode="decimal" style={{ ...inputStyle, width: 100 }} /></label>
                <label style={{ display: 'grid', gap: 3, fontSize: 12, flex: 1 }}><span>Référence</span><input value={reference} onChange={(e) => setReference(e.target.value)} style={inputStyle} placeholder="Ex. : Ricos et al., Scand J Clin Lab Invest 1999, mise à jour 2014" /></label>
              </div>
            )}
          </div>

          <div style={bloc(source === 'aucune')}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 700, fontSize: 13 }}>
              <input type="radio" checked={source === 'aucune'} onChange={() => setSource('aucune')} />
              Aucune — la performance est déclarée non calculable, motif nommé
            </label>
          </div>
        </div>
      )}
    </Modale>
  );
}

/* ═══════════════════════════ FILTRES COMMUNS (R5 → R7) ═══════════════════════════ */

type Filtres = { du: string; au: string; automate: string; discipline: string; analyte: string; lot: string; niveau: string; operateur: string; statut: string };
const plusJours = (iso: string, n: number) => isoJour(new Date(Date.parse(`${iso}T00:00:00Z`) + n * 86400000));
const filtresDefaut = (): Filtres => ({ du: aujourdhuiIso(), au: aujourdhuiIso(), automate: '', discipline: '', analyte: '', lot: '', niveau: '', operateur: '', statut: '' });
const enQuery = (f: Filtres) => new URLSearchParams(Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ''))).toString();
/** Le périmètre, en clair — l'en-tête OBLIGATOIRE de tout export. */
function phraseFiltres(f: Record<string, unknown>): string {
  const bouts = [`du ${fmtDate(String(f.du))} au ${fmtDate(String(f.au))}`];
  for (const [k, lib] of [['automate', 'automate'], ['discipline', 'discipline'], ['analyte', 'paramètre'], ['lot', 'lot de contrôle'], ['niveau', 'niveau'], ['operateur', 'opérateur'], ['statut', 'statut']] as const) {
    if (f[k] !== undefined && f[k] !== null && f[k] !== '') bouts.push(`${lib} : ${f[k]}`);
  }
  return bouts.join(' · ');
}

function BarreFiltres({ f, onChange, cartes }: { f: Filtres; onChange: (f: Filtres) => void; cartes: CarteFiltre[] }) {
  const { settings } = useGed();
  const set = (k: keyof Filtres, v: string) => onChange({ ...f, [k]: v });
  const equips = [...new Map(cartes.map((c) => [c.equipement, c.equipementNom])).entries()].sort();
  const disciplines = [...new Set(cartes.map((c) => c.discipline).filter((x): x is string => !!x))].sort();
  const analytes = [...new Map(cartes.map((c) => [c.analyte, c.analyteNom])).entries()].sort((a, b) => a[1].localeCompare(b[1], 'fr'));
  const auj = aujourdhuiIso();
  const rapide = (du: string, au: string) => onChange({ ...f, du, au });
  const sel = (k: keyof Filtres, label: string, opts: [string, string][]) => (
    <label style={{ display: 'grid', gap: 3, fontSize: 11.5 }}>
      <span>{label}</span>
      <select value={f[k]} onChange={(e) => set(k, e.target.value)} style={{ ...inputStyle, width: 170, fontSize: 12.5 }}>
        <option value="">Tous</option>
        {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
  return (
    <div style={{ ...card, padding: 12, display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>Période :</span>
        {(() => {
          const RAPIDES = [
            { k: 'jour', l: 'Aujourd’hui', du: auj, au: auj }, { k: '30j', l: '30 jours', du: plusJours(auj, -29), au: auj }, { k: '12m', l: '12 mois', du: plusJours(auj, -364), au: auj },
          ];
          const courante = RAPIDES.find((p) => f.du === p.du && f.au === p.au)?.k || 'libre';
          return (
            <Segment ariaLabel="Période rapide" valeur={courante} onChange={(v) => { const p = RAPIDES.find((x) => x.k === v); if (p) rapide(p.du, p.au); }}
              options={[...RAPIDES.map((p) => ({ valeur: p.k, libelle: p.l })), { valeur: 'libre', libelle: 'du … au', aide: 'Dates choisies à la main', disabled: courante !== 'libre' }]} />
          );
        })()}
        <label style={{ fontSize: 11.5, display: 'flex', gap: 4, alignItems: 'center' }}>du <input type="date" value={f.du} onChange={(e) => set('du', e.target.value)} style={{ ...inputStyle, width: 150, fontSize: 12.5 }} /></label>
        <label style={{ fontSize: 11.5, display: 'flex', gap: 4, alignItems: 'center' }}>au <input type="date" value={f.au} onChange={(e) => set('au', e.target.value)} style={{ ...inputStyle, width: 150, fontSize: 12.5 }} /></label>
        {f.au >= auj && <span style={{ fontSize: 11, color: 'var(--amber-600)', fontWeight: 600 }}>période entamée — jamais comparée à une période complète</span>}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {sel('automate', 'Automate', equips.map(([c, n]) => [c, `${c} · ${n}`]))}
        {sel('discipline', 'Discipline', disciplines.map((d) => [d, d]))}
        {sel('analyte', 'Paramètre', analytes.map(([c, n]) => [c, n]))}
        <label style={{ display: 'grid', gap: 3, fontSize: 11.5 }}><span>Lot de contrôle</span><input value={f.lot} onChange={(e) => set('lot', e.target.value)} style={{ ...inputStyle, width: 130, fontSize: 12.5 }} placeholder="exact" /></label>
        {sel('niveau', 'Niveau', [['1', '1'], ['2', '2'], ['3', '3'], ['4', '4']])}
        {sel('operateur', 'Opérateur', (settings?.gedUsers || []).map((u) => [u.id, u.nom]))}
        {sel('statut', 'Statut', [['conforme', 'Conforme'], ['alerte', 'Avertissement'], ['rejet', 'Rejet'], ['baseline', 'Baseline'], ['annule', 'Annulé']])}
      </div>
    </div>
  );
}

/* ═══════════════════════════ R5 — TABLEAU DE BORD ═══════════════════════════ */

type Tdb = {
  periode: { du: string; au: string; entamee: boolean; granularite: string };
  indicateurs: Record<string, number | null>;
  series: { cle: string; libelle: string; debut: string; fin: string; tronquee: boolean; entamee: boolean; passages: number; conformes: number; alertes: number; rejets: number; rejetsSansConduiteTenue: number }[];
  filtresAppliques: Record<string, unknown>; calculeLe: string;
  /** La période précédente de même longueur — `null` quand la période est entamée (jamais comparée). */
  precedent: { du: string; au: string; indicateurs: Record<string, number | null> } | null;
  comparaisonMotif: string | null;
};
const INDICATEURS: { k: string; label: string; aide: string; statut?: string; grave?: boolean }[] = [
  { k: 'passages', label: 'Passages', aide: 'Passages non annulés sur la période' },
  { k: 'conformes', label: 'Conformes', aide: 'Statut courant conforme', statut: 'conforme' },
  { k: 'alertes', label: 'Avertissements', aide: 'Statut courant alerte', statut: 'alerte' },
  { k: 'rejets', label: 'Rejets', aide: 'Statut courant rejet', statut: 'rejet', grave: true },
  { k: 'rejetsSansConduiteTenue', label: 'Rejets sans conduite tenue', aide: 'Rejet sans cause + action + responsable : depuis le 08/10/2026, ou encore en attente d’une suite', statut: 'rejet', grave: true },
  { k: 'rejetsSansConduiteAnterieurs', label: 'Rejets antérieurs, soldés sans conduite écrite', aide: 'Avant la mise en service de la conduite à tenir (08/10/2026) et déjà soldés par dérogation ou repasse : un enregistrement d’avant l’outil, pas un manquement', statut: 'rejet' },
  { k: 'rejetsEnAttente', label: 'Rejets en attente d’une suite', aide: 'Ni dérogation, ni repasse, ni calibration', statut: 'rejet' },
  { k: 'derogations', label: 'Dérogations', aide: 'Passages validés par dérogation, ou dérogation de lot' },
  { k: 'baselineEnCours', label: 'Cartes en baseline', aide: 'Cible interne demandée, pas approuvée : aucune règle évaluée', statut: 'baseline' },
  { k: 'pointsSansCible', label: 'Points sans cible', aide: 'Valeur saisie sur un niveau sans cible ou sans écart-type' },
  { k: 'verdictsRevises', label: 'Verdicts révisés', aide: 'Verdict courant différent du verdict d’origine (R2)' },
  { k: 'annules', label: 'Passages annulés', aide: 'Annulés avec motif — hors statistiques', statut: 'annule' },
  { k: 'ncOuvertesLieesCq', label: 'NC ouvertes liées au CQ', aide: 'Non-conformités déclarées depuis un passage, non closes' },
  { k: 'lotsControleExpires', label: 'Lots de contrôle inutilisables', aide: 'Lots ouverts périmés ou hors période au jour de la lecture' },
  { k: 'lotsControleSous60j', label: 'Lots sous 60 jours', aide: 'Péremption dans les 60 jours' },
  { k: 'cartesSansPassageSurLaPeriode', label: 'Cartes sans passage', aide: 'Cartes actives du périmètre sans aucun passage sur la période' },
];

function TableauDeBord({ f, onChange, cartes, onVoirAudit }: { f: Filtres; onChange: (f: Filtres) => void; cartes: CarteFiltre[]; onVoirAudit: (f: Filtres) => void }) {
  const [tdb, setTdb] = useState<Tdb | null>(null);
  const [erreur, setErreur] = useState('');
  const [forme, setForme] = useState<Forme>('barres');
  const [serie, setSerie] = useState<'rejets' | 'alertes' | 'passages' | 'rejetsSansConduiteTenue'>('rejets');
  // Autres filtres : le tableau précédent s'efface pendant la requête. Recalé PENDANT LE RENDU (motif de `ui.tsx`), pas dans l'effet.
  const [filtresVus, setFiltresVus] = useState(f);
  if (filtresVus !== f) { setFiltresVus(f); setTdb(null); setErreur(''); }
  useEffect(() => {
    let vivant = true;
    void lire(`/api/ged/ged/cq/tableau-de-bord?${enQuery(f)}`)
      .then(async (r) => (r.ok ? r.clone().json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j: Tdb) => { if (vivant) setTdb(j); })
      .catch((e) => { if (vivant) { setErreur(`Le tableau de bord n’a pas pu être lu (${e.message}). Ce n’est pas un zéro.`); setTdb({ periode: { du: f.du, au: f.au, entamee: false, granularite: '' }, indicateurs: {}, series: [], filtresAppliques: f, calculeLe: '', precedent: null, comparaisonMotif: null }); } });
    return () => { vivant = false; };
  }, [f]);
  const points: PointKpi[] = useMemo(() => (tdb?.series || []).map((s) => ({
    cle: s.cle, libelle: s.libelle, debut: s.debut, fin: s.fin, tronquee: s.entamee,
    valeurs: Object.fromEntries((['passages', 'conformes', 'alertes', 'rejets', 'rejetsSansConduiteTenue'] as const).map((k) => [k, { valeur: s[k], calculable: true, motif: '', texte: String(s[k]), numerateur: s[k], denominateur: null }])),
  })), [tdb]);
  const def: DefKpi = { cle: serie, libelle: INDICATEURS.find((i) => i.k === serie)?.label || serie, unite: 'nombre', aide: '', sensSuggere: 'au_plus' };
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <BarreFiltres f={f} onChange={onChange} cartes={cartes} />
      {erreur && <div style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--red-50)', color: 'var(--red-600)', fontSize: 12.5 }}>{erreur}</div>}
      {/* Le graphe D'ABORD (règle de la page KPI), sur le socle de Kpi.tsx. */}
      <div style={{ ...card, padding: 12, display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={serie} onChange={(e) => setSerie(e.target.value as typeof serie)} style={{ ...inputStyle, width: 240, fontSize: 12.5 }} aria-label="Série tracée">
            <option value="rejets">Rejets</option><option value="alertes">Avertissements</option><option value="passages">Passages</option><option value="rejetsSansConduiteTenue">Rejets sans conduite tenue</option>
          </select>
          <Segment ariaLabel="Forme du graphe" valeur={forme} onChange={(v) => setForme(v as Forme)} options={[{ valeur: 'barres', libelle: 'Barres' }, { valeur: 'courbe', libelle: 'Courbe' }, { valeur: 'aires', libelle: 'Aires' }]} />
          <span style={{ flex: 1 }} />
          {tdb?.periode.granularite && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>par {tdb.periode.granularite}{tdb.periode.entamee ? ' · période entamée (hachurée)' : ''}</span>}
        </div>
        {tdb === null ? <Skeleton rows={2} height={80} /> : points.length ? <Graphe points={points} cle={serie} def={def} forme={forme} /> : <EmptyState titre="Aucun passage sur la période" indice="Élargissez la période ou le périmètre." />}
      </div>
      {/* Chaque indicateur est CLIQUABLE vers l'audit filtré : un indicateur non cliquable est de la décoration. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 8 }}>
        {INDICATEURS.map((ind) => {
          const v = tdb?.indicateurs?.[ind.k];
          const n = typeof v === 'number' ? v : null;
          return (
            <button key={ind.k} type="button" title={`${ind.aide} — voir les passages dans l’audit`}
              onClick={() => onVoirAudit({ ...f, statut: ind.statut || f.statut })}
              style={{ ...card, padding: '10px 12px', textAlign: 'left', cursor: 'pointer', borderColor: ind.grave && n ? 'var(--red-200)' : undefined, background: ind.grave && n ? 'var(--red-50)' : undefined }}>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--font-mono)', color: ind.grave && n ? 'var(--red-600)' : 'var(--text-strong)' }}>
                {tdb === null ? '…' : n === null ? '—' : n}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-body)', fontWeight: 600 }}>{ind.label}</div>
              <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>{ind.aide}</div>
              {/* La tendance : l'écart avec la période précédente de même longueur,
                  seulement quand la période est complète (règle de la page KPI). */}
              {tdb?.precedent && typeof tdb.precedent.indicateurs[ind.k] === 'number' && n !== null && !['lotsControleExpires', 'lotsControleSous60j', 'cartesSansPassageSurLaPeriode', 'ncOuvertesLieesCq', 'baselineEnCours'].includes(ind.k) && (() => {
                const avant = tdb.precedent!.indicateurs[ind.k] as number; const d = n - avant;
                const mauvais = ind.grave || ['alertes', 'rejetsEnAttente', 'derogations', 'pointsSansCible', 'annules', 'rejetsSansConduiteAnterieurs'].includes(ind.k);
                const couleur = d === 0 ? 'var(--text-muted)' : (d > 0) === mauvais ? 'var(--red-600)' : 'var(--green-600)';
                return <div style={{ fontSize: 10.5, fontWeight: 700, color: couleur, marginTop: 2 }} title={`Période précédente (${fmtDate(tdb.precedent!.du)} → ${fmtDate(tdb.precedent!.au)}) : ${avant}`}>{d > 0 ? '+' : ''}{d} vs période précédente ({avant})</div>;
              })()}
            </button>
          );
        })}
      </div>
      {tdb?.comparaisonMotif && <div style={{ fontSize: 11.5, color: 'var(--amber-600)' }}>{tdb.comparaisonMotif}</div>}
      {tdb && (
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
          Taux de conformité : <strong>{tdb.indicateurs.tauxConformite ?? '—'} %</strong> · taux de rejet : <strong>{tdb.indicateurs.tauxRejet ?? '—'} %</strong> · |z| moyen : <strong>{tdb.indicateurs.zMoyenAbs ?? '—'}</strong>
          {' '}· {tdb.indicateurs.cartes} carte(s) dans le périmètre · périmètre : {phraseFiltres(tdb.filtresAppliques)} · calculé le {tdb.calculeLe ? fmtDate(tdb.calculeLe) : '—'}. Recalculé à chaque lecture — ce n’est pas un constat figé.
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════ R7 — AUDIT MESURE PAR MESURE ═══════════════════════════ */

type LigneAudit = {
  passageId: string; date: string; numeroSerie: number | null; annule: boolean; annuleMotif: string | null;
  carteId: string; analyte: string; analyteNom: string; equipement: string; equipementNom: string; technique: string | null;
  discipline: string | null; unite: string | null; lotControle: string | null; periodeId: string; lotReactif: string | null;
  niveau: number; niveauLibelle: string; valeur: number | null; texte: string | null; qualitatif: boolean;
  cible: number | null; sd: number | null; sourceCible: string | null; z: number | null;
  regles: string[]; statut: string | null; statutOrigine: string | null; verdictRevise: boolean;
  operateur: string | null; saisiPar: string | null; validation: string | null; motifDerogation: string | null;
  conduite: string | null; aTraiter: boolean; enAttente: boolean; ncRef: string | null; baseline: boolean;
  rejete: boolean; rejeteMotif: string | null; commentaire: string | null;
};
type Audit = { lignes: LigneAudit[]; total: number; filtresAppliques: Record<string, unknown>; calculeLe: string; editePar: { key: string; nom: string }; peutExporter: boolean };

const COLONNES_AUDIT: { k: string; label: string; v: (l: LigneAudit, nom: (k: string | null) => string) => string }[] = [
  { k: 'date', label: 'Date', v: (l) => `${l.date}${l.numeroSerie ? ` n°${l.numeroSerie}` : ''}` },
  { k: 'carte', label: 'Carte', v: (l) => `${l.analyteNom} (${l.analyte})` },
  { k: 'equipement', label: 'Automate / technique', v: (l) => `${l.equipement} ${l.equipementNom}${l.technique ? ` · ${l.technique}` : ''}` },
  { k: 'lotControle', label: 'Lot de contrôle', v: (l) => l.lotControle || '' },
  { k: 'lotReactif', label: 'Lot de réactif', v: (l) => l.lotReactif || '' },
  { k: 'niveau', label: 'Niveau', v: (l) => l.niveauLibelle },
  { k: 'valeur', label: 'Valeur', v: (l) => (l.qualitatif ? (l.texte || '') : (l.valeur === null ? '' : `${l.valeur}${l.unite ? ` ${l.unite}` : ''}`)) },
  { k: 'cible', label: 'Cible', v: (l) => (l.cible === null ? '' : String(l.cible)) },
  { k: 'sd', label: 'σ', v: (l) => (l.sd === null ? '' : String(l.sd)) },
  { k: 'sourceCible', label: 'Source de cible', v: (l) => l.sourceCible || '' },
  { k: 'z', label: 'z', v: (l) => (l.z === null ? '' : l.z.toFixed(2)) },
  { k: 'regles', label: 'Règles déclenchées', v: (l) => l.regles.join(' ') },
  { k: 'statut', label: 'Statut courant', v: (l) => (l.annule ? 'annulé' : l.statut || '') },
  { k: 'statutOrigine', label: 'Statut d’origine', v: (l) => l.statutOrigine || '' },
  { k: 'operateur', label: 'Opérateur', v: (l, nom) => nom(l.operateur) },
  { k: 'saisiPar', label: 'Saisi par', v: (l, nom) => (l.saisiPar ? nom(l.saisiPar) : '') },
  { k: 'validation', label: 'Validation', v: (l) => l.validation || '' },
  { k: 'derogation', label: 'Dérogation + motif', v: (l) => l.motifDerogation || '' },
  { k: 'conduite', label: 'Conduite tenue', v: (l) => (l.statut === 'rejet' ? (l.aTraiter ? 'à traiter' : l.conduite || '') : '') },
  { k: 'nc', label: 'NC liée', v: (l) => l.ncRef || '' },
  { k: 'annulation', label: 'Annulation + motif', v: (l) => (l.annule ? l.annuleMotif || 'annulé' : '') },
  { k: 'commentaire', label: 'Commentaire', v: (l) => l.commentaire || '' },
];

/** Un statut INCOMPLET : « Pourquoi ? » (donnée reçue / attendue) et « Que faire ? ». */
function pourquoiQueFaire(l: LigneAudit): { pourquoi: string; queFaire: string } | null {
  if (l.annule) return { pourquoi: `Passage annulé — ${l.annuleMotif || 'motif non renseigné'}.`, queFaire: 'Rien : le point est hors statistiques, le journal porte le motif.' };
  if (l.baseline) return { pourquoi: 'Cible interne demandée mais non approuvée : aucune règle évaluée.', queFaire: 'Approuver la cible interne sur la carte (ou revenir à la cible fournisseur).' };
  if (!l.qualitatif && (l.cible === null || !(l.sd && l.sd > 0))) return { pourquoi: 'Aucune cible ou écart-type exploitable pour ce niveau : z non calculable.', queFaire: 'Renseigner le lot de contrôle et ses valeurs assignées sur la carte.' };
  if (l.statut === 'rejet' && l.aTraiter) return { pourquoi: 'Rejet sans cause, action et responsable.', queFaire: 'Renseigner la conduite tenue sur le point (carte → point → conduite).' };
  if (l.statut === 'rejet' && l.enAttente) return { pourquoi: 'Rejet sans suite : ni dérogation, ni repasse, ni calibration.', queFaire: 'Solder : dérogation motivée, repasse, ou calibration.' };
  return null;
}

function telecharger(nom: string, contenu: string, type: string) {
  const blob = new Blob(['﻿' + contenu], { type });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = nom; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
const csvCell = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
const htmlEsc = (v: string) => String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** PDF d'un bloc d'écran : `jspdf` + `html-to-image`, patron de `RhPlanningMois.tsx`. */
async function exporterPdf(el: HTMLElement, nom: string) {
  const [{ toPng }, { jsPDF }] = await Promise.all([import('html-to-image'), import('jspdf')]);
  const png = await toPng(el, { pixelRatio: 2, backgroundColor: 'var(--surface-card)' });
  const img = new Image(); img.src = png; await img.decode();
  const paysage = img.width > img.height;
  const doc = new jsPDF({ orientation: paysage ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });
  const w = doc.internal.pageSize.getWidth() - 10; const h = doc.internal.pageSize.getHeight() - 10;
  const r = Math.min(w / img.width, h / img.height);
  doc.addImage(png, 'PNG', 5, 5, img.width * r, img.height * r);
  doc.save(nom);
}

function AuditCq({ f, onChange, cartes, onOuvrirCarte }: { f: Filtres; onChange: (f: Filtres) => void; cartes: CarteFiltre[]; onOuvrirCarte: (carteId: string) => void }) {
  const { settings, session } = useGed();
  const nom = useCallback((k: string | null) => settings?.gedUsers?.find((u) => u.id === k)?.nom || k || '', [settings?.gedUsers]);
  const [audit, setAudit] = useState<Audit | null>(null);
  const [erreur, setErreur] = useState('');
  const [recherche, setRecherche] = useState('');
  const zone = React.useRef<HTMLDivElement | null>(null);
  // Autres filtres : l'audit précédent s'efface pendant la requête. Recalé PENDANT LE RENDU (motif de `ui.tsx`), pas dans l'effet.
  const cleAudit = JSON.stringify([f, session.key, session.name]);
  const [auditVu, setAuditVu] = useState(cleAudit);
  if (auditVu !== cleAudit) { setAuditVu(cleAudit); setAudit(null); setErreur(''); }
  useEffect(() => {
    let vivant = true;
    void lire(`/api/ged/ged/cq/audit?${enQuery(f)}`)
      .then(async (r) => (r.ok ? r.clone().json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j: Audit) => { if (vivant) setAudit(j); })
      .catch((e) => { if (vivant) { setErreur(`L’audit n’a pas pu être lu (${e.message}). Ce n’est pas une liste vide.`); setAudit({ lignes: [], total: 0, filtresAppliques: f, calculeLe: '', editePar: { key: session.key || '', nom: session.name || '' }, peutExporter: false }); } });
    return () => { vivant = false; };
  }, [f, session.key, session.name]);
  /* La recherche porte sur TOUTES les colonnes telles qu'elles s'exportent :
     ce qu'on lit est ce qu'on cherche. L'export suit la recherche. */
  const lignes = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    const toutes = audit?.lignes || [];
    if (!q) return toutes;
    return toutes.filter((l) => COLONNES_AUDIT.some((c) => c.v(l, nom).toLowerCase().includes(q)));
  }, [audit, recherche, nom]);
  const entete = () => audit ? `Audit CQ — ${phraseFiltres(audit.filtresAppliques)}${recherche.trim() ? ` · recherche « ${recherche.trim()} »` : ''} — ${lignes.length} ligne(s)${lignes.length !== audit.total ? ` sur ${audit.total}` : ''} — édité par ${audit.editePar.nom} (${audit.editePar.key}) le ${fmtDateHeure(new Date())}` : '';
  const exportCsv = () => {
    if (!audit) return;
    const corps = [[entete()].map(csvCell).join(';'), COLONNES_AUDIT.map((c) => csvCell(c.label)).join(';'),
      ...lignes.map((l) => COLONNES_AUDIT.map((c) => csvCell(c.v(l, nom))).join(';'))];
    telecharger(`audit-cq-${f.du}-${f.au}.csv`, corps.join('\n'), 'text/csv;charset=utf-8');
  };
  const exportExcel = () => {
    if (!audit) return;
    const html = `<html><head><meta charset="utf-8"></head><body><p>${htmlEsc(entete())}</p><table ref={enTeteCollant} className="ged-tableau" border="1"><tr>${COLONNES_AUDIT.map((c) => `<th scope="col">${htmlEsc(c.label)}</th>`).join('')}</tr>`
      + lignes.map((l) => `<tr>${COLONNES_AUDIT.map((c) => `<td>${htmlEsc(c.v(l, nom))}</td>`).join('')}</tr>`).join('') + '</table></body></html>';
    telecharger(`audit-cq-${f.du}-${f.au}.xls`, html, 'application/vnd.ms-excel');
  };
  const nombre = (v: string) => { const n = Number(v.replace(',', '.').replace(/[^0-9.+-]/g, '')); return v === '' || !Number.isFinite(n) ? null : n; };
  const colonnes: ColonneTriable<LigneAudit>[] = useMemo(() => [
    ...COLONNES_AUDIT.map((c): ColonneTriable<LigneAudit> => ({
      cle: c.k, libelle: c.label,
      valeur: (l) => (['valeur', 'cible', 'sd', 'z', 'niveau'].includes(c.k) ? (nombre(c.v(l, nom)) ?? Number.NEGATIVE_INFINITY) : c.v(l, nom)),
      style: { whiteSpace: c.k === 'commentaire' || c.k === 'derogation' ? 'normal' : 'nowrap', fontFamily: ['valeur', 'cible', 'sd', 'z', 'regles', 'lotControle', 'lotReactif'].includes(c.k) ? 'var(--font-mono)' : undefined },
      rendu: (l) => {
        const ton = l.annule ? { bg: 'var(--surface-sunken)', fg: 'var(--text-muted)' } : l.statut ? TONS_STATUT[l.statut] : undefined;
        if (c.k === 'carte') return <button type="button" onClick={() => onOuvrirCarte(l.carteId)} title="Ouvrir la carte" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-primary-active)', fontWeight: 700 }}>{c.v(l, nom)}</button>;
        if (c.k === 'statut' && ton) return <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 8px', background: ton.bg, color: ton.fg }}>{c.v(l, nom)}{l.verdictRevise ? ' ↺' : ''}</span>;
        return <span style={{ opacity: l.annule ? 0.7 : 1 }}>{c.v(l, nom)}</span>;
      },
    })),
    {
      cle: 'pourquoi', libelle: 'Pourquoi ? / Que faire ?', style: { minWidth: 260 },
      valeur: (l) => pourquoiQueFaire(l)?.pourquoi || '',
      rendu: (l) => {
        const pq = pourquoiQueFaire(l);
        if (!pq) return null;
        return (
          <div style={{ fontSize: 11.5, display: 'grid', gap: 2 }}>
            <span><strong>Pourquoi ?</strong> {pq.pourquoi}</span>
            <button type="button" onClick={() => onOuvrirCarte(l.carteId)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-primary-active)', textAlign: 'left', fontSize: 11.5 }}>
              <strong>Que faire ?</strong> {pq.queFaire} <I n="arrow" s={10} />
            </button>
          </div>
        );
      },
    },
  ], [nom, onOuvrirCarte]);
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <BarreFiltres f={f} onChange={onChange} cartes={cartes} />
      {erreur && <div style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--red-50)', color: 'var(--red-600)', fontSize: 12.5 }}>{erreur}</div>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13 }}>{audit === null ? 'Lecture…' : `${lignes.length} ligne(s) (passage × niveau)${lignes.length !== audit.total ? ` sur ${audit.total}` : ''} — toute la sélection`}</strong>
        <input value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Rechercher dans la sélection (paramètre, lot, règle, opérateur, commentaire…)" aria-label="Rechercher dans l’audit" style={{ ...inputStyle, width: 340, fontSize: 12.5 }} />
        <span style={{ flex: 1 }} />
        {audit && (
          <>
            <Bouton taille="sm" onClick={exportCsv} disabled={!audit.peutExporter || !lignes.length} title={!audit.peutExporter ? 'Exporter exige le droit « cqiRapporter » (ou « imprimer »)' : 'CSV de TOUTE la sélection (recherche comprise), avec son périmètre en en-tête'}>Exporter (CSV)</Bouton>
            <Bouton taille="sm" onClick={exportExcel} disabled={!audit.peutExporter || !lignes.length} title={!audit.peutExporter ? 'Exporter exige le droit « cqiRapporter » (ou « imprimer »)' : 'Fichier Excel (.xls) de TOUTE la sélection, périmètre en en-tête'}>Exporter (Excel)</Bouton>
            <Bouton taille="sm" onClick={() => zone.current && void exporterPdf(zone.current, `audit-cq-${f.du}-${f.au}.pdf`)} disabled={!audit.peutExporter || !lignes.length} title={!audit.peutExporter ? 'Exporter exige le droit « cqiRapporter » (ou « imprimer »)' : 'PDF de la page affichée, périmètre en en-tête'}>PDF</Bouton>
          </>
        )}
      </div>
      <div ref={zone} style={{ display: 'grid', gap: 0 }}>
        {audit && <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--text-muted)', borderBottom: '1px solid var(--border-default)' }}>{entete()}</div>}
        {audit === null ? <Skeleton rows={6} /> : !lignes.length ? (
          <EmptyState titre={audit.total && recherche ? 'Aucune ligne ne contient ce texte' : 'Aucun passage dans cette sélection'} indice="Élargissez la période, le périmètre ou la recherche — les filtres appliqués sont rappelés ci-dessus." />
        ) : (
          /* Le socle commun des registres : tri par colonne, ordre des colonnes
             mémorisé, pagination. L'export, lui, porte sur TOUTE la sélection. */
          <Tableau<LigneAudit> id="cq-audit" colonnes={colonnes} lignes={lignes}
            cleLigne={(l) => `${l.passageId}-${l.niveau}`} parPage={100} defilementHorizontal quoi="ligne(s)" />
        )}
      </div>
    </div>
  );
}
const TONS_STATUT: Record<string, { bg: string; fg: string }> = {
  conforme: { bg: 'var(--green-50)', fg: 'var(--green-600)' }, alerte: { bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  rejet: { bg: 'var(--red-50)', fg: 'var(--red-600)' }, baseline: { bg: 'var(--cyan-50)', fg: 'var(--cyan-700)' },
};

/* ═══════════════════════════ R8 — SYNTHÈSE MENSUELLE ═══════════════════════════ */

type Synthese = {
  annee: number;
  mois: { cle: string; libelle: string; entame: boolean; aVenir: boolean; passages: number; tauxConformite: number | null; tauxRejet: number | null; zMoyenAbs: number | null; alertes: number; rejets: number; derogations: number; rejetsSansConduiteTenue: number; baselinesApprouvees: number; ncOuvertes: number; ncCloses: number; lotsControleExpires: number }[];
  moyennes: { surMois: number; tauxConformite: number | null; tauxRejet: number | null; zMoyenAbs: number | null };
  filtresAppliques: Record<string, unknown>; calculeLe: string; editePar: { key: string; nom: string }; peutExporter: boolean;
};

function SyntheseMensuelle({ cartes }: { cartes: CarteFiltre[] }) {
  const [annee, setAnnee] = useState(Number(aujourdhuiIso().slice(0, 4)));
  const [automate, setAutomate] = useState('');
  const [s, setS] = useState<Synthese | null>(null);
  const [erreur, setErreur] = useState('');
  const zone = React.useRef<HTMLDivElement | null>(null);
  // Autre année ou automate : la synthèse précédente s'efface pendant la requête. Recalé PENDANT LE RENDU (motif de `ui.tsx`), pas dans l'effet.
  const cleSynthese = `${annee}|${automate}`;
  const [syntheseVue, setSyntheseVue] = useState(cleSynthese);
  if (syntheseVue !== cleSynthese) { setSyntheseVue(cleSynthese); setS(null); setErreur(''); }
  useEffect(() => {
    let vivant = true;
    void lire(`/api/ged/ged/cq/synthese-mensuelle?annee=${annee}${automate ? `&automate=${encodeURIComponent(automate)}` : ''}`)
      .then(async (r) => (r.ok ? r.clone().json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j: Synthese) => { if (vivant) setS(j); })
      .catch((e) => { if (vivant) setErreur(`La synthèse n’a pas pu être lue (${e.message}).`); });
    return () => { vivant = false; };
  }, [annee, automate]);
  const equips = [...new Map(cartes.map((c) => [c.equipement, c.equipementNom])).entries()].sort();
  const COLS: { k: keyof Synthese['mois'][number]; label: string; pct?: boolean }[] = [
    { k: 'passages', label: 'Passages' }, { k: 'tauxConformite', label: 'Conformité %', pct: true }, { k: 'tauxRejet', label: 'Rejet %', pct: true },
    { k: 'zMoyenAbs', label: '|z| moyen' }, { k: 'alertes', label: 'Avertissements' }, { k: 'rejets', label: 'Rejets' },
    { k: 'baselinesApprouvees', label: 'Baselines approuvées' }, { k: 'derogations', label: 'Dérogations' }, { k: 'rejetsSansConduiteTenue', label: 'Rejets sans conduite' },
    { k: 'ncOuvertes', label: 'NC ouvertes' }, { k: 'ncCloses', label: 'NC closes' }, { k: 'lotsControleExpires', label: 'Lots de contrôle expirés' },
  ];
  const entete = () => s ? `Synthèse mensuelle CQ ${s.annee} — ${phraseFiltres(s.filtresAppliques)} — édité par ${s.editePar.nom} (${s.editePar.key}) le ${fmtDateHeure(new Date())}` : '';
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ ...card, padding: 12, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ display: 'grid', gap: 3, fontSize: 11.5 }}><span>Année</span><input type="number" value={annee} onChange={(e) => setAnnee(Number(e.target.value) || annee)} style={{ ...inputStyle, width: 100 }} /></label>
        <label style={{ display: 'grid', gap: 3, fontSize: 11.5 }}><span>Automate</span>
          <select value={automate} onChange={(e) => setAutomate(e.target.value)} style={{ ...inputStyle, width: 220 }}><option value="">Tous</option>{equips.map(([c, n]) => <option key={c} value={c}>{c} · {n}</option>)}</select>
        </label>
        <span style={{ flex: 1 }} />
        {s && <Bouton taille="sm" onClick={() => zone.current && void exporterPdf(zone.current, `synthese-cq-${s.annee}.pdf`)} disabled={!s.peutExporter} title={!s.peutExporter ? 'Exporter exige le droit « cqiRapporter » (ou « imprimer »)' : 'PDF de la synthèse, périmètre en en-tête'}>PDF</Bouton>}
      </div>
      {erreur && <div style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--red-50)', color: 'var(--red-600)', fontSize: 12.5 }}>{erreur}</div>}
      <div ref={zone} style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {s && <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--text-muted)', borderBottom: '1px solid var(--border-default)' }}>{entete()}</div>}
        {s === null ? <Skeleton rows={6} /> : (
          <table ref={enTeteCollant} className="ged-tableau" style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1100, fontSize: 12.5 }}>
            <thead><tr style={{ fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}><th scope="col" style={{ textAlign: 'left', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>Mois</th>{COLS.map((c) => <th scope="col" key={c.k} style={{ textAlign: 'right', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>{c.label}</th>)}</tr></thead>
            <tbody>
              {s.mois.map((m) => (
                <tr key={m.cle} style={{ borderTop: '1px solid var(--border-default)', opacity: m.aVenir ? 0.45 : 1, background: m.entame ? 'repeating-linear-gradient(45deg, transparent 0 6px, var(--surface-sunken) 6px 12px)' : undefined }}>
                  <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', fontWeight: 600 }}>{m.libelle}{m.entame ? <span style={{ marginLeft: 6, fontSize: 10.5, color: 'var(--amber-600)', fontWeight: 700 }}>entamé — exclu des moyennes</span> : m.aVenir ? <span style={{ marginLeft: 6, fontSize: 10.5, color: 'var(--text-muted)' }}>à venir</span> : null}</td>
                  {COLS.map((c) => { const v = m[c.k]; return <td key={c.k} style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'right', fontFamily: 'var(--font-mono)', color: c.k === 'rejetsSansConduiteTenue' && typeof v === 'number' && v > 0 ? 'var(--red-600)' : undefined }}>{m.aVenir ? '' : v === null || v === undefined ? '—' : typeof v === 'number' ? v : String(v)}</td>; })}
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid var(--border-default)', fontWeight: 700 }}>
                <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>Moyenne sur {s.moyennes.surMois} mois complet(s)</td>
                {COLS.map((c) => <td key={c.k} style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{c.k === 'tauxConformite' ? (s.moyennes.tauxConformite ?? '—') : c.k === 'tauxRejet' ? (s.moyennes.tauxRejet ?? '—') : c.k === 'zMoyenAbs' ? (s.moyennes.zMoyenAbs ?? '—') : ''}</td>)}
              </tr>
            </tbody>
          </table>
        )}
      </div>
      <p style={{ margin: 0, fontSize: 11.5, color: 'var(--text-muted)' }}>Alimente la revue de direction (§8.8 / §8.9) et le dossier d’audit. Un mois entamé est marqué et exclu des moyennes ; un mois à venir n’est pas compté.</p>
    </div>
  );
}

/* ═══════════════════════════ L'ÉCRAN ═══════════════════════════ */

export function QualiteAnalytique() {
  const { naviguerVers, cartesCqi } = useGed();
  const [onglet, setOnglet] = useState<Onglet>('tdb');
  const [filtres, setFiltres] = useState<Filtres>(filtresDefaut);
  /* Les listes de filtres viennent du MAGASIN (les résumés de cartes, déjà
     chargés pour la barre latérale) : aucun balayage de performance n'est
     lancé pour remplir un menu déroulant. */
  const cartes: CarteFiltre[] = useMemo(() => cartesCqi.map((c) => ({
    equipement: c.equipement, equipementNom: c.equipementNom, discipline: c.discipline || null, analyte: c.analyte, analyteNom: c.analyteNom,
  })), [cartesCqi]);
  const ouvrirCarte = (carteId: string) => naviguerVers('cqi', { cle: 'selectedCqiCarte', valeur: carteId });
  /* Le titre de l'onglet du navigateur suit la VUE, pas seulement l'écran (lot F) :
     quatre onglets ouverts côte à côte doivent se distinguer. */
  useEffect(() => {
    const label = ONGLETS.find((o) => o.k === onglet)?.label || '';
    document.title = `Qualité analytique — ${label} — Nafis`;
  }, [onglet]);
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <H1 eyebrow="Performance analytique · ISO 15189 §7.3.7" title="Qualité analytique" />
      {/* Choisir une vue n'est pas une action : `Segment`, jamais `Bouton` (UI-CONVENTIONS § 1). */}
      <Segment ariaLabel="Vue de la qualité analytique" valeur={onglet} onChange={(v) => setOnglet(v as Onglet)}
        options={ONGLETS.map((o) => ({ valeur: o.k, libelle: o.label }))} />
      {onglet === 'tdb' && <TableauDeBord f={filtres} onChange={setFiltres} cartes={cartes} onVoirAudit={(f) => { setFiltres(f); setOnglet('audit'); }} />}
      {onglet === 'perf' && <Performance onOuvrirCarte={ouvrirCarte} />}
      {onglet === 'audit' && <AuditCq f={filtres} onChange={setFiltres} cartes={cartes} onOuvrirCarte={ouvrirCarte} />}
      {onglet === 'synthese' && <SyntheseMensuelle cartes={cartes} />}
    </div>
  );
}
